import React, { useMemo } from "react";
import {
    IWorkShiftTimeSlot,
    getWorkShiftTimeSlotDurationStr,
} from "../../models/workShiftTimeSlot/workShiftTimeSlot";
import { Box, Card, CardContent, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Base } from "../../framework/common/base";
import { LinkIconButton } from "../../framework/common/linkButton";
import { generatePath } from "react-router-dom";
import { WorkShiftTimeSlotState } from "../../models/common/enums";
import { WorkTimeAutomationsIndicator } from "./workTimeAutomationsIndicator";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import { optionOwnerHasCustomWorkTimeAllowed } from "../../models/common/consts";
import { useAppSelector } from "../../framework/redux/hooks";

interface IWorkTimeEntryCardProps {
    date: string;
    item: IWorkShiftTimeSlot;
    overlapping: boolean;
    handleDelete: (id: string) => void;
}

const workTimeInfoArray = (item: IWorkShiftTimeSlot) => {
    const formattedArray = [
        item.vehicleRegisterNumber,
        item.customerName,
        item.costCenterName,
        item.workTimeTypeName,
        item.salaryRowTypeName,
    ]
        .filter((value) => value)
        .join(", ");
    return formattedArray;
};

const formatTime = (time: string, date: string) =>
    Base.isSameDay(Base.dateStrToDayjsIgnoreTimezone(time), date)
        ? Base.dateStrToOriginalTimezoneTimeStr(time)
        : Base.dateStrToOriginalTimezoneDateTimeStr(time);

export const WorkTimeEntryCard = ({
    date,
    item,
    overlapping,
    handleDelete,
}: IWorkTimeEntryCardProps) => {
    const [customTypeEnabled] = useAppOptionValueEquals(
        optionOwnerHasCustomWorkTimeAllowed,
        "1"
    );

    const canEditWorkEntries = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.canEditWorkEntries
    );

    const canEditCustomType = customTypeEnabled || !item.isCustomType;
    const canEdit = item.state === WorkShiftTimeSlotState.Done &&
        canEditCustomType &&
        canEditWorkEntries;
    const canDelete = item.state !== WorkShiftTimeSlotState.Locked &&
        canEditCustomType &&
        canEditWorkEntries;

    const timeStr = useMemo(() => {
        const offset = Base.dateTzOffsetIfNotLocal(
            item.startDate,
            item.timeZoneName
        );

        return item.state === WorkShiftTimeSlotState.InProgress
            ? `${formatTime(item.startDate, date)}${
                  offset ? ` ${offset}` : ""
              } - `
            : `${formatTime(item.startDate, date)} - ${formatTime(
                  item.endDate,
                  date
              )}${offset ? ` ${offset}` : ""}`;
    }, [date, item.startDate, item.endDate, item.state]);
    const onDelete = () => handleDelete(item.id);

    return (
        <Card sx={{ mb: 2, bgcolor: "lightGreen.main" }}>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <Box
                        sx={{
                            textAlign: "left",
                        }}
                    >
                        <Typography
                            variant="h5"
                            color={overlapping ? "error.main" : "primary.dark"}
                            gutterBottom
                        >
                            {timeStr}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            textAlign: "right",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <Typography variant="h5" color="primary.dark">
                            {getWorkShiftTimeSlotDurationStr(item)}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                    }}
                >
                    <Box
                        sx={{
                            textAlign: "left",
                        }}
                    >
                        <WorkTimeAutomationsIndicator slot={item} />
                        <Typography variant="subtitle2" color="primary.dark">
                            {workTimeInfoArray(item)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                        }}
                    >
                        {canEdit && (
                            <LinkIconButton
                                to={generatePath(
                                    `/work/form${
                                        item.isCustomType ? "/other" : ""
                                    }/edit/:id/:date?`,
                                    {
                                        id: item.id,
                                        date,
                                    }
                                )}
                            >
                                <EditIcon color="primary" />
                            </LinkIconButton>
                        )}
                        {canDelete ? (
                            <DeleteForeverIcon
                                color="error"
                                onClick={onDelete}
                            />
                        ) : null}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};
